import GalleryImageType from '~/constants/vdp/galleryImageType';
import type {
  Feature,
  GalleryImage,
  ImageLocation,
  Imperfection,
  Spin,
  SpinImage
} from '~/contracts/vdp';

type MaybeWithProperties = {
  imageUrl?: string;
  location?: ImageLocation;
  title?: string;
};

function hasRequiredProperties<T extends MaybeWithProperties>(
  image: T
): image is T & Required<Pick<T, 'imageUrl' | 'location' | 'title'>> {
  return Boolean(
    image.imageUrl?.trim() && image.location?.trim() && image.title?.trim()
  );
}

export function hasImageUrl<T extends { imageUrl?: string }>(
  image: T
): image is T & Required<Pick<T, 'imageUrl'>> {
  return Boolean(image.imageUrl?.trim());
}

export function transformFeatures(features: Feature[]): GalleryImage[] {
  return features.filter(hasRequiredProperties).map<GalleryImage>(v => ({
    id: String(v.id),
    imageUrl: v.imageUrl,
    location: v.location,
    title: v.title,
    type: GalleryImageType.Feature
  }));
}

export function transformImperfections(
  imperfections: Imperfection[]
): GalleryImage[] {
  return imperfections.filter(hasRequiredProperties).map<GalleryImage>(v => ({
    compressedVideo: v.compressedVideo,
    id: String(v.id),
    imageUrl: v.imageUrl,
    location: v.location,
    title: v.title,
    type: GalleryImageType.Imperfection,
    videoUrl: v.videoUrl,
    zoneDescription: v.zoneDescription
  }));
}

export const edcAngles = [2, 1, 0, 4, 8, 10, 14];

export const edoAngles = [2, 8];

export function transformExteriorImages(spins: Spin[]): GalleryImage[] {
  const allDoorsClosedImages =
    spins.find(spin => spin.style === 'edc' && spin.cameraHeight === 5)
      ?.images || [];

  const doorsClosedImages = edcAngles.reduce<SpinImage[]>((acc, angle) => {
    const image = allDoorsClosedImages.find(v => v.positionId === angle);

    if (image) acc.push(image);

    return acc;
  }, []);

  const allDoorsOpenImages =
    spins.find(spin => spin.style === 'edo' && spin.cameraHeight === 7)
      ?.images || [];

  const doorsOpenImages = edoAngles.reduce<SpinImage[]>((acc, angle) => {
    const image = allDoorsOpenImages.find(v => v.positionId === angle);

    if (image) acc.push(image);

    return acc;
  }, []);

  return doorsClosedImages
    .concat(doorsOpenImages)
    .filter(hasImageUrl)
    .map<GalleryImage>((v, index) => ({
      id: `exterior-${index}`,
      imageUrl: v.imageUrl,
      location: 'exterior',
      positionId: v.positionId,
      title: '',
      type: GalleryImageType.Exterior
    }));
}
